import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from "react-native";

import moment from "moment";
import Dialog from "../../common/dialog";
import AppTheme from "../../AppTheme";
import Icons from "../../common/icons/index";
import MoodColors from "../../common/mood/MoodColors";
import ManaButton from "../../common/ManaButton";
import appStyles from "../../../appStyles";
import Utils from "../../../../helpers/utils";
import CalendarTimePicker from "../../common/CalendarTimePicker";

const DATE_FORMAT = "YYYY-MM-DD HH:mm";

export default function SnoozeIntegrationDialog({
  controller,
  account,
  integrationName,
  saveIntegrations = () => {},
  onCancel = () => {},
}) {
  const now = Utils.nowUserTz(controller); // moment().tz("UTC");
  const [selectedAccount, setSelectedAccount] = useState(account);
  const [showCalendar, setShowCalendar] = useState(false);

  // ------------------------------------- METHODS -------------------------------------
  useEffect(() => {
    setSelectedAccount(account);
  }, [account]);

  const getLaterThisWeek = (currentDate, isFriday) => {
    const endDay = isFriday ? 6 : 5;

    while (currentDate.day() !== endDay) {
      currentDate.add(1, "day");
    }
    return currentDate;
  };

  const onSnooze = (option) => {
    if (showCalendar) setShowCalendar(false);

    // Cancel (revert to enabled state)
    if (option == null) {
      onCancel(selectedAccount);
      // Close dialog
      setSelectedAccount(null);
    } else if (option.hasCalendar) {
      setShowCalendar(true);
    } else {
      // Close dialog
      setSelectedAccount(null);
      let data = {
        account: selectedAccount.email || account.id,
        [integrationName]: false,
        id: account.id,
      };
      if (option.date != null) {
        data.snooze = option.name;
        if (option.date !== "off") {
          data.snooze_date = moment(option.date).utc().format(DATE_FORMAT);
        } else {
          data.snooze_date = option.date;
        }
      }

      saveIntegrations(data, true);
    }
  };

  // ------------------------------------- OPTIONS -------------------------------------
  const nf = getLaterThisWeek(now.clone(), now.weekday() == 5);

  const dates = {
    oneHourLater: now.clone().add(1, "hours"),
    laterToday: now.clone().add(4, "hours"),
    tomorrow: now.clone().add(1, "days"),
    laterThisWeek: nf.hours(8).minutes(0),
    nextWeek: now.clone().add(1, "weeks").hours(8).minutes(0),
  };

  const options = [
    {
      icon: Icons.Clock,
      name: "An hour from now",
      date: dates.oneHourLater.format(DATE_FORMAT),
      dateText: dates.oneHourLater.format("h:mm a"),
    },
    {
      icon: Icons.SnoozeLater,
      name: "Later today",
      date: dates.laterToday.format(DATE_FORMAT),
      dateText: dates.laterToday.format("h:mm a"),
    },
    {
      icon: Icons.SnoozeTomorrow,
      name: "Tomorrow",
      date: dates.tomorrow.format(DATE_FORMAT),
      dateText: dates.tomorrow.format("h:mm a"),
    },
    {
      icon: Icons.SnoozeLaterThisWeek,
      name: "Later this week",
      date: dates.laterThisWeek.format(DATE_FORMAT),
      dateText: dates.laterThisWeek.format("ddd h:mm a"),
    },
    {
      icon: Icons.SnoozeLater,
      name: "Next Week",
      date: dates.nextWeek.format(DATE_FORMAT),
      dateText: dates.nextWeek.format("ddd h:mm a"),
    },
    {
      icon: Icons.HourGlassIcon,
      name: "Pick Date & Time",
      hasCalendar: true,
    },
  ];

  return (
    <>
      {selectedAccount ? (
        <Dialog.Container
          showCloseButton
          onClose={() => onSnooze(null)}
          headerStyle={{ margin: 0 }}
          contentStyle={{
            paddingTop: 0,
            marginTop: 0,
            paddingBottom: 0,
            paddingHorizontal: 0,
          }}
        >
          <View style={styles.card}>
            <Text style={styles.title}>Turn Off Integration Until</Text>

            {/* Content */}
            <View style={styles.cardContent}>
              {showCalendar ? (
                <CalendarTimePicker
                  controller={controller}
                  confirm={onSnooze}
                />
              ) : (
                <>
                  {options.map((option, index) => (
                    <TouchableOpacity
                      key={`option_${index}`}
                      onPress={() => onSnooze(option)}
                      style={[styles.listItem, appStyles.borderBottom]}
                    >
                      <option.icon />
                      <Text style={[styles.itemName, { flex: 1 }]}>
                        {option.name}
                      </Text>
                      <Text style={styles.itemName}>{option.dateText}</Text>
                    </TouchableOpacity>
                  ))}

                  <View style={styles.buttons}>
                    <Text onPress={() => onSnooze({ date: "off" })} style={styles.danger}>
                      Turn off until I turn it back on
                    </Text>

                    <ManaButton
                      onPress={() => onSnooze(null)}
                      style={styles.button}
                      styleText={styles.buttonText}
                      text={"Cancel"}
                    />
                  </View>
                </>
              )}
            </View>
          </View>
        </Dialog.Container>
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  close: {
    position: "absolute",
    top: 5,
    right: 5,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    paddingBottom: 10,
  },
  title: {
    paddingVertical: 10,
    paddingLeft: 8,
    width: "100%",
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
  cardContent: {
    display: "flex",
    alignItems: "center",
    paddingHorizontal: 8,
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    height: 40,
    width: "100%",
  },
  itemName: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.5,
    color: AppTheme.almostBlack,
    marginLeft: 8,
  },
  danger: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 10,
    lineHeight: 12,
    letterSpacing: 0.175,
    color: MoodColors.VeryBad,
    height: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  buttons: {
    width: "100%",
    paddingHorizontal: 8,
    alignItems: "center",
  },
  button: {
    width: "100%",
    maxWidth: 180,
    height: 32,
  },
  buttonText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "500",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: AppTheme.DEFAULT_COLOR,
  },
});
